/**
 * Scheduled tasks ('tasks.*') and the admin access controls for them.
 * The Arabic table must cover every key.
 */
const en = {
  'nav.tasks': 'Tasks',

  'tasks.title': 'Scheduled tasks',
  'tasks.subtitle': 'Have an agent or an AI model run a prompt for you on a schedule. Results arrive as a chat in your sidebar — and by email if you like.',
  'tasks.new': 'New task',
  'tasks.usage': '{n} of {max} active',
  'tasks.loadFailed': 'Could not load your tasks.',
  'tasks.disabledTitle': 'Scheduled tasks are not enabled for your account',
  'tasks.disabledBody': 'Ask an administrator to turn them on for you.',
  'tasks.emptyTitle': 'No tasks yet',
  'tasks.emptyBody': 'Create one to get a daily briefing, a weekly report, or a reminder — answered by the agent of your choice.',

  'tasks.statusActive': 'Active',
  'tasks.statusPaused': 'Paused',
  'tasks.statusDone': 'Completed',
  'tasks.statusRunning': 'Running…',
  'tasks.next': 'Next run',
  'tasks.last': 'Last run',
  'tasks.never': 'Not run yet',
  'tasks.run.succeeded': 'Succeeded',
  'tasks.run.failed': 'Failed',
  'tasks.run.skipped': 'Skipped',
  'tasks.run.manual': 'Run manually',

  'tasks.runNow': 'Run now',
  'tasks.pause': 'Pause',
  'tasks.resume': 'Resume',
  'tasks.edit': 'Edit',
  'tasks.openChat': 'Open chat',
  'tasks.history': 'History',
  'tasks.delete': 'Delete',
  'tasks.actionsFor': 'Actions for {name}',

  'tasks.started': 'The task is running — the result will appear in its chat.',
  'tasks.paused': 'Task paused',
  'tasks.resumed': 'Task resumed',
  'tasks.saved': 'Task saved',
  'tasks.created': 'Task created',
  'tasks.deleted': 'Task deleted',
  'tasks.actionFailed': 'Something went wrong. Please try again.',
  'tasks.deleteTitle': 'Delete "{name}"?',
  'tasks.deleteMessage': 'The task stops running. Its chat and past results are kept.',
  'tasks.deleteConfirm': 'Delete task',

  // Schedule summaries
  'tasks.sched.once': 'Once, {when}',
  'tasks.sched.daily': 'Every day at {time}',
  'tasks.sched.weekly': 'Every {days} at {time}',
  'tasks.sched.weekdays': 'Every weekday at {time}',
  'tasks.sched.monthly': 'Monthly on day {day} at {time}',
  'tasks.sched.zone': '({zone})',

  // Form
  'tasks.createTitle': 'New scheduled task',
  'tasks.editTitle': 'Edit task',
  'tasks.form.title': 'Name',
  'tasks.form.titlePlaceholder': 'e.g. Monday sales summary',
  'tasks.form.agent': 'Agent',
  'tasks.form.agentPlaceholder': 'Choose an agent',
  'tasks.form.noAgents': 'You don\'t have access to any agents yet.',
  'tasks.form.prompt': 'Prompt',
  'tasks.form.promptPlaceholder': 'What should the agent do each time? e.g. "Summarise last week\'s open support tickets by priority."',
  'tasks.form.promptHint': 'Sent as you — your access and data policies apply. Variables like {{date}} are filled in at run time.',
  'tasks.form.repeat': 'Repeat',
  'tasks.form.type.once': 'Once',
  'tasks.form.type.daily': 'Daily',
  'tasks.form.type.weekly': 'Weekly',
  'tasks.form.type.monthly': 'Monthly',
  'tasks.form.runAt': 'Date and time',
  'tasks.form.time': 'Time',
  'tasks.form.days': 'Days',
  'tasks.form.dayOfMonth': 'Day of month',
  'tasks.form.dayOfMonthHint': 'In shorter months, days 29–31 run on the last day.',
  'tasks.form.timeZone': 'Time zone',
  'tasks.form.save': 'Save task',
  'tasks.form.create': 'Create task',
  'tasks.form.pickDay': 'Choose at least one day.',

  // History
  'tasks.historyTitle': 'Run history — {name}',
  'tasks.historyEmpty': 'This task has not run yet.',

  // v2: models, hourly, email, extras
  'tasks.sched.hourly': 'Every {n} hours from {time}',
  'tasks.sched.hourlyOne': 'Every hour from {time}',
  'tasks.sched.onDays': 'on {days}',
  'tasks.form.type.hourly': 'Hourly',
  'tasks.form.every': 'Every (hours)',
  'tasks.form.firstRun': 'First run each day',
  'tasks.form.onlyDays': 'Only on these days (optional)',
  'tasks.form.engine': 'Answered by',
  'tasks.form.engineAgent': 'An agent',
  'tasks.form.engineModel': 'An AI model',
  'tasks.form.model': 'Model',
  'tasks.form.modelPlaceholder': 'Choose a model',
  'tasks.form.modelSearch': 'Filter models…',
  'tasks.form.modelsLoading': 'Loading models…',
  'tasks.form.noModels': 'No AI models are available. Ask an administrator to enable Amazon Bedrock access.',
  'tasks.form.modelProfile': 'cross-region',
  'tasks.form.instructions': 'Instructions (optional)',
  'tasks.form.instructionsPlaceholder': 'How the model should answer, e.g. "You are a concise financial analyst. Use bullet points."',
  'tasks.form.advanced': 'Advanced model settings',
  'tasks.form.temperature': 'Creativity',
  'tasks.form.temperatureHint': 'Lower is more focused and repeatable; higher is more varied.',
  'tasks.form.temperatureDefault': 'Model default',
  'tasks.form.temperatureCustom': 'Custom: {v}',
  'tasks.form.maxTokens': 'Max reply length (tokens)',
  'tasks.form.maxTokensHint': 'Empty = {n}.',
  'tasks.form.variables': 'Insert:',
  'tasks.form.variablesHint': 'Variables are filled in when the task runs, in the task\'s time zone.',
  'tasks.form.templates': 'Start from an idea',
  'tasks.form.upcoming': 'Upcoming runs',
  'tasks.form.upcomingNone': 'No upcoming runs.',
  'tasks.form.email': 'Email the result',
  'tasks.form.notify.none': 'Don\'t email',
  'tasks.form.notify.always': 'After every run',
  'tasks.form.notify.success': 'When it succeeds',
  'tasks.form.notify.failure': 'Only if it fails',
  'tasks.form.notify.changed': 'Only when the answer meaningfully changes',
  'tasks.form.emailOff': 'Email isn\'t set up on this server yet.',
  'tasks.form.recipients': 'Recipients',
  'tasks.form.recipientsSelf': 'Me ({email})',
  'tasks.form.recipientsSearch': 'Add people by name or email…',
  'tasks.form.recipientsAddEmail': 'Add {email}',
  'tasks.form.recipientsDomains': 'You can also type addresses at {domains}.',
  'tasks.form.recipientsMax': 'Up to {n} recipients.',
  'tasks.form.recipientsIdentity': 'Each email shows your name as the sender, says the task is yours, and replies come back to you.',
  'tasks.form.recipientRemove': 'Remove {name}',
  'tasks.form.noResults': 'No matching people.',
  'tasks.form.testEmail': 'Send me a test email',
  'tasks.form.testEmailSent': 'Test email sent — check your inbox.',
  'tasks.form.pickRecipient': 'Choose at least one recipient.',
  'tasks.form.pickModel': 'Choose a model.',
  'tasks.duplicate': 'Duplicate',
  'tasks.copySuffix': '(copy)',
  'tasks.emailsTo': 'Emails {n}',
  'tasks.run.emailSent': 'Emailed {n}',
  'tasks.run.emailFailed': 'Email failed',
  'tasks.run.tokens': '{in} in · {out} out tokens',
  'tasks.run.seconds': '{s}s',

  // v3: drafting from text, agentic runs, files, judged notifications
  'tasks.draft.title': 'Task from your message',
  'tasks.draft.thinking': 'Working out what and when…',
  'tasks.draft.failed': 'Could not draft a task from that.',
  'tasks.draft.firstRuns': 'First runs:',
  'tasks.draft.done.pause': 'Task paused',
  'tasks.draft.done.resume': 'Task resumed',
  'tasks.draft.done.run': 'Task started',
  'tasks.draft.done.delete': 'Task deleted',
  'tasks.draft.confirm.pause': 'Pause "{name}"',
  'tasks.draft.confirm.resume': 'Resume "{name}"',
  'tasks.draft.confirm.run': 'Run "{name}" now',
  'tasks.draft.confirm.delete': 'Delete "{name}"',
  'tasks.draft.suggest': 'Schedule this as a task',
  'tasks.draft.scheduleButton': 'Schedule as a task',
  'tasks.draft.scheduleHint': 'Type what should happen and when, then schedule it',
  'tasks.draft.describeLabel': 'Describe a task',
  'tasks.draft.describePlaceholder': 'e.g. "Every weekday at 8, brief me on news about our competitors and email me"',
  'tasks.draft.describeButton': 'Draft it',
  'tasks.draft.review': 'Drafted from your description — check the details, then create it.',
  'tasks.badge.web': 'Web',
  'tasks.badge.connectors': 'Connectors',
  'tasks.form.tools': 'Tools',
  'tasks.form.allowWeb': 'Web access',
  'tasks.form.allowWebHint': 'Can search the web and read pages and online documents.',
  'tasks.form.allowWebNoSearch': 'Can read web pages and documents by URL. Web search isn\'t configured on this server.',
  'tasks.form.allowConnectors': 'My connectors',
  'tasks.form.allowConnectorsHint': 'Can use: {names}',
  'tasks.form.noConnectors': 'No connectors are enabled. Add one from the chat composer.',
  'tasks.form.connectorsDisabled': 'Connectors are not enabled for your account, so this task runs without them. Ask an administrator for access.',
  'tasks.form.output': 'Deliver as a file',
  'tasks.form.outputNone': 'No file — text only',
  'tasks.form.output.docx': 'Word document (.docx)',
  'tasks.form.output.xlsx': 'Excel sheet (.xlsx)',
  'tasks.form.output.csv': 'CSV (.csv)',
  'tasks.form.output.md': 'Markdown (.md)',
  'tasks.form.output.html': 'Web page (.html)',
  'tasks.form.outputHint': 'The file is attached to the result in the chat and to the email.',
  'tasks.form.notify.condition': 'Only if…',
  'tasks.form.notifyCondition': 'Email condition',
  'tasks.form.notifyConditionPlaceholder': 'e.g. "a contract expires within 30 days" or "the price drops below 100"',
  'tasks.form.notifyJudgeHint': 'An AI checks each result and only emails when it matters. Failed runs are always emailed.',
  'tasks.form.pickCondition': 'Describe when you want to be emailed.',
  'tasks.run.steps': '{n} tool call(s)',
  'tasks.tool.web_search': 'Web search',
  'tasks.tool.fetch_url': 'Read page',
  'tasks.tool.create_file': 'Create file',
  'tasks.draft.to': 'to {who}',
  'tasks.draft.me': 'you',
  'tasks.draft.people': '{n} colleague(s)',
  'tasks.form.emailInPromptHint': 'Writing an address in the prompt doesn\'t send email. Choose when to email above and add recipients.',

  // Templates
  'tasks.tpl.briefing': 'Morning briefing',
  'tasks.tpl.briefingPrompt': 'Give me a short briefing for {{weekday}} {{date}}: the three things I should focus on today, and anything I might be forgetting.',
  'tasks.tpl.weekly': 'Weekly summary',
  'tasks.tpl.weeklyPrompt': 'Summarise the week ending {{date}} in five bullet points, then list open risks and next steps.',
  'tasks.tpl.changes': 'Tell me what changed',
  'tasks.tpl.changesPrompt': 'Check again and tell me only what is new or different compared with your previous answer. If nothing changed, say so in one line.\n\nPrevious answer:\n{{last_result}}',
  'tasks.tpl.standup': 'Standup agenda',
  'tasks.tpl.standupPrompt': 'Draft a 10-minute standup agenda for {{weekday}} {{date}} with time boxes.',

  // Admin-published templates ("Recommended")
  'tasks.templates.heading': 'Recommended',
  'tasks.templates.manage': 'Manage templates',
  'tasks.templates.doneManaging': 'Done',
  'tasks.templates.new': 'New template',
  'tasks.templates.empty': 'No templates yet.',
  'tasks.templates.atCap': "You've reached your active task limit.",
  'tasks.templates.builtIn': 'Built-in',
  'tasks.templates.hidden': 'Hidden',
  'tasks.templates.edit': 'Edit template {name}',
  'tasks.templates.hide': 'Hide template {name} from users',
  'tasks.templates.publish': 'Show template {name} to users',
  'tasks.templates.hideShort': 'Hide from users',
  'tasks.templates.publishShort': 'Show to users',
  'tasks.templates.delete': 'Delete template {name}',
  'tasks.templates.hiddenToast': 'Template hidden from users',
  'tasks.templates.publishedToast': 'Template is now visible to users',
  'tasks.templates.deleteTitle': 'Delete the template "{name}"?',
  'tasks.templates.deleteMessage': 'Users will no longer see it. Tasks already created from it keep running.',
  'tasks.templates.deleted': 'Template deleted',
  'tasks.templates.saved': 'Template saved',
  'tasks.templates.newTitle': 'New template',
  'tasks.templates.editTitle': 'Edit template',
  'tasks.templates.review': 'Started from the "{name}" template. Review the details, then create the task.',
  'tasks.templates.editorNote': 'Everyone with task access sees published templates under Recommended and can create a task from one in a click.',
  'tasks.templates.titleEn': 'Name (English)',
  'tasks.templates.titleAr': 'Name (Arabic, optional)',
  'tasks.templates.icon': 'Icon (emoji)',
  'tasks.templates.description': 'Description',
  'tasks.templates.descriptionPlaceholder': 'One line on what users get, e.g. "Weekly rebar and cement prices as a spreadsheet."',
  'tasks.templates.descriptionAr': 'Description (Arabic, optional)',
  'tasks.templates.publishedLabel': 'Show to users',
  'tasks.templates.defaultModel': "Default model (each user's default)",
  'tasks.templates.recipientsHint': 'Results go to whoever creates a task from this template; they can add more recipients.',
  'tasks.templates.save': 'Save template',

  // Admin (user dialog)
  'tasks.admin.section': 'Scheduled tasks',
  'tasks.admin.enable': 'Allow this user to create scheduled tasks',
  'tasks.admin.max': 'Max active tasks',
  'tasks.admin.maxDefault': 'Default ({n})',
  'tasks.admin.maxHint': 'Leave empty for the default. Turning access off pauses this user\'s tasks immediately.',
  'tasks.admin.inUse': '{active} active of {total} task(s)',
  'tasks.admin.updateFailed': 'The user was saved, but task access could not be updated.',
};

const ar: Record<keyof typeof en, string> = {
  'nav.tasks': 'المهام',

  'tasks.title': 'المهام المجدولة',
  'tasks.subtitle': 'اجعل وكيلًا أو نموذج ذكاء اصطناعي ينفّذ طلبًا نيابةً عنك وفق جدول زمني. تصلك النتائج كمحادثة في الشريط الجانبي — وبالبريد الإلكتروني إن رغبت.',
  'tasks.new': 'مهمة جديدة',
  'tasks.usage': '{n} من {max} نشطة',
  'tasks.loadFailed': 'تعذّر تحميل مهامك.',
  'tasks.disabledTitle': 'المهام المجدولة غير مفعّلة لحسابك',
  'tasks.disabledBody': 'اطلب من أحد المسؤولين تفعيلها لك.',
  'tasks.emptyTitle': 'لا توجد مهام بعد',
  'tasks.emptyBody': 'أنشئ مهمة لتحصل على موجز يومي أو تقرير أسبوعي أو تذكير — يجيب عنه الوكيل الذي تختاره.',

  'tasks.statusActive': 'نشطة',
  'tasks.statusPaused': 'متوقفة مؤقتًا',
  'tasks.statusDone': 'مكتملة',
  'tasks.statusRunning': 'قيد التنفيذ…',
  'tasks.next': 'التشغيل التالي',
  'tasks.last': 'آخر تشغيل',
  'tasks.never': 'لم تُشغَّل بعد',
  'tasks.run.succeeded': 'نجح',
  'tasks.run.failed': 'فشل',
  'tasks.run.skipped': 'تم التخطي',
  'tasks.run.manual': 'تشغيل يدوي',

  'tasks.runNow': 'شغّل الآن',
  'tasks.pause': 'إيقاف مؤقت',
  'tasks.resume': 'استئناف',
  'tasks.edit': 'تعديل',
  'tasks.openChat': 'فتح المحادثة',
  'tasks.history': 'السجل',
  'tasks.delete': 'حذف',
  'tasks.actionsFor': 'إجراءات {name}',

  'tasks.started': 'المهمة قيد التنفيذ — ستظهر النتيجة في محادثتها.',
  'tasks.paused': 'تم إيقاف المهمة مؤقتًا',
  'tasks.resumed': 'تم استئناف المهمة',
  'tasks.saved': 'تم حفظ المهمة',
  'tasks.created': 'تم إنشاء المهمة',
  'tasks.deleted': 'تم حذف المهمة',
  'tasks.actionFailed': 'حدث خطأ ما. يُرجى المحاولة مرة أخرى.',
  'tasks.deleteTitle': 'حذف "{name}"؟',
  'tasks.deleteMessage': 'ستتوقف المهمة عن التشغيل. تبقى محادثتها ونتائجها السابقة.',
  'tasks.deleteConfirm': 'حذف المهمة',

  'tasks.sched.once': 'مرة واحدة، {when}',
  'tasks.sched.daily': 'يوميًا الساعة {time}',
  'tasks.sched.weekly': 'كل {days} الساعة {time}',
  'tasks.sched.weekdays': 'كل أيام العمل الساعة {time}',
  'tasks.sched.monthly': 'شهريًا في اليوم {day} الساعة {time}',
  'tasks.sched.zone': '({zone})',

  'tasks.createTitle': 'مهمة مجدولة جديدة',
  'tasks.editTitle': 'تعديل المهمة',
  'tasks.form.title': 'الاسم',
  'tasks.form.titlePlaceholder': 'مثال: ملخص مبيعات يوم الإثنين',
  'tasks.form.agent': 'الوكيل',
  'tasks.form.agentPlaceholder': 'اختر وكيلًا',
  'tasks.form.noAgents': 'ليس لديك وصول إلى أي وكيل بعد.',
  'tasks.form.prompt': 'الطلب',
  'tasks.form.promptPlaceholder': 'ماذا يجب أن يفعل الوكيل في كل مرة؟ مثال: "لخّص تذاكر الدعم المفتوحة للأسبوع الماضي حسب الأولوية."',
  'tasks.form.promptHint': 'يُرسَل باسمك — تنطبق صلاحياتك وسياسات البيانات الخاصة بك. تُملأ المتغيرات مثل {{date}} عند التشغيل.',
  'tasks.form.repeat': 'التكرار',
  'tasks.form.type.once': 'مرة واحدة',
  'tasks.form.type.daily': 'يومي',
  'tasks.form.type.weekly': 'أسبوعي',
  'tasks.form.type.monthly': 'شهري',
  'tasks.form.runAt': 'التاريخ والوقت',
  'tasks.form.time': 'الوقت',
  'tasks.form.days': 'الأيام',
  'tasks.form.dayOfMonth': 'يوم الشهر',
  'tasks.form.dayOfMonthHint': 'في الأشهر الأقصر، تُشغَّل الأيام 29–31 في آخر يوم من الشهر.',
  'tasks.form.timeZone': 'المنطقة الزمنية',
  'tasks.form.save': 'حفظ المهمة',
  'tasks.form.create': 'إنشاء المهمة',
  'tasks.form.pickDay': 'اختر يومًا واحدًا على الأقل.',

  'tasks.historyTitle': 'سجل التشغيل — {name}',
  'tasks.historyEmpty': 'لم تُشغَّل هذه المهمة بعد.',

  // v2: models, hourly, email, extras
  'tasks.sched.hourly': 'كل {n} ساعات بدءًا من {time}',
  'tasks.sched.hourlyOne': 'كل ساعة بدءًا من {time}',
  'tasks.sched.onDays': 'أيام {days}',
  'tasks.form.type.hourly': 'كل ساعة',
  'tasks.form.every': 'كل (ساعات)',
  'tasks.form.firstRun': 'أول تشغيل كل يوم',
  'tasks.form.onlyDays': 'في هذه الأيام فقط (اختياري)',
  'tasks.form.engine': 'من يجيب',
  'tasks.form.engineAgent': 'وكيل',
  'tasks.form.engineModel': 'نموذج ذكاء اصطناعي',
  'tasks.form.model': 'النموذج',
  'tasks.form.modelPlaceholder': 'اختر نموذجًا',
  'tasks.form.modelSearch': 'تصفية النماذج…',
  'tasks.form.modelsLoading': 'جارٍ تحميل النماذج…',
  'tasks.form.noModels': 'لا تتوفر نماذج ذكاء اصطناعي. اطلب من أحد المسؤولين تفعيل الوصول إلى Amazon Bedrock.',
  'tasks.form.modelProfile': 'متعدد المناطق',
  'tasks.form.instructions': 'تعليمات (اختياري)',
  'tasks.form.instructionsPlaceholder': 'كيف يجب أن يجيب النموذج، مثال: "أنت محلل مالي موجز. استخدم نقاطًا."',
  'tasks.form.advanced': 'إعدادات النموذج المتقدمة',
  'tasks.form.temperature': 'الإبداع',
  'tasks.form.temperatureHint': 'القيمة الأقل أكثر تركيزًا وثباتًا، والأعلى أكثر تنوعًا.',
  'tasks.form.temperatureDefault': 'افتراضي النموذج',
  'tasks.form.temperatureCustom': 'مخصص: {v}',
  'tasks.form.maxTokens': 'الحد الأقصى لطول الرد (رموز)',
  'tasks.form.maxTokensHint': 'فارغ = {n}.',
  'tasks.form.variables': 'إدراج:',
  'tasks.form.variablesHint': 'تُملأ المتغيرات عند تشغيل المهمة، بحسب المنطقة الزمنية للمهمة.',
  'tasks.form.templates': 'ابدأ من فكرة',
  'tasks.form.upcoming': 'التشغيلات القادمة',
  'tasks.form.upcomingNone': 'لا توجد تشغيلات قادمة.',
  'tasks.form.email': 'إرسال النتيجة بالبريد الإلكتروني',
  'tasks.form.notify.none': 'لا ترسل',
  'tasks.form.notify.always': 'بعد كل تشغيل',
  'tasks.form.notify.success': 'عند النجاح',
  'tasks.form.notify.failure': 'عند الفشل فقط',
  'tasks.form.notify.changed': 'فقط عندما تتغير الإجابة',
  'tasks.form.emailOff': 'البريد الإلكتروني غير مُعدّ على هذا الخادم بعد.',
  'tasks.form.recipients': 'المستلمون',
  'tasks.form.recipientsSelf': 'أنا ({email})',
  'tasks.form.recipientsSearch': 'أضف أشخاصًا بالاسم أو البريد…',
  'tasks.form.recipientsAddEmail': 'إضافة {email}',
  'tasks.form.recipientsDomains': 'يمكنك أيضًا كتابة عناوين في {domains}.',
  'tasks.form.recipientsMax': 'حتى {n} مستلمين.',
  'tasks.form.recipientsIdentity': 'يظهر اسمك كمرسل في كل رسالة، ويُذكر أن المهمة تخصك، وتصلك الردود.',
  'tasks.form.recipientRemove': 'إزالة {name}',
  'tasks.form.noResults': 'لا يوجد أشخاص مطابقون.',
  'tasks.form.testEmail': 'أرسل لي رسالة تجريبية',
  'tasks.form.testEmailSent': 'أُرسلت الرسالة التجريبية — تحقق من بريدك.',
  'tasks.form.pickRecipient': 'اختر مستلمًا واحدًا على الأقل.',
  'tasks.form.pickModel': 'اختر نموذجًا.',
  'tasks.duplicate': 'نسخ',
  'tasks.copySuffix': '(نسخة)',
  'tasks.emailsTo': 'يُرسل إلى {n}',
  'tasks.run.emailSent': 'أُرسل إلى {n}',
  'tasks.run.emailFailed': 'فشل الإرسال',
  'tasks.run.tokens': '{in} إدخال · {out} إخراج',
  'tasks.run.seconds': '{s} ث',

  // v3: drafting from text, agentic runs, files, judged notifications
  'tasks.draft.title': 'مهمة من رسالتك',
  'tasks.draft.thinking': 'جارٍ تحديد ما يجب فعله ومتى…',
  'tasks.draft.failed': 'تعذّر إنشاء مسودة مهمة من ذلك.',
  'tasks.draft.firstRuns': 'أول التشغيلات:',
  'tasks.draft.done.pause': 'أُوقفت المهمة مؤقتًا',
  'tasks.draft.done.resume': 'استؤنفت المهمة',
  'tasks.draft.done.run': 'بدأ تشغيل المهمة',
  'tasks.draft.done.delete': 'حُذفت المهمة',
  'tasks.draft.confirm.pause': 'إيقاف "{name}" مؤقتًا',
  'tasks.draft.confirm.resume': 'استئناف "{name}"',
  'tasks.draft.confirm.run': 'تشغيل "{name}" الآن',
  'tasks.draft.confirm.delete': 'حذف "{name}"',
  'tasks.draft.suggest': 'جدولة هذا كمهمة',
  'tasks.draft.scheduleButton': 'جدولة كمهمة',
  'tasks.draft.scheduleHint': 'اكتب ما يجب أن يحدث ومتى، ثم قم بجدولته',
  'tasks.draft.describeLabel': 'صف مهمة',
  'tasks.draft.describePlaceholder': 'مثال: "كل يوم عمل الساعة 8، لخّص لي أخبار منافسينا وأرسلها لي بالبريد"',
  'tasks.draft.describeButton': 'أنشئ المسودة',
  'tasks.draft.review': 'أُنشئت المسودة من وصفك — راجع التفاصيل ثم أنشئ المهمة.',
  'tasks.badge.web': 'الويب',
  'tasks.badge.connectors': 'الموصلات',
  'tasks.form.tools': 'الأدوات',
  'tasks.form.allowWeb': 'الوصول إلى الويب',
  'tasks.form.allowWebHint': 'يمكنه البحث في الويب وقراءة الصفحات والمستندات المنشورة.',
  'tasks.form.allowWebNoSearch': 'يمكنه قراءة صفحات الويب والمستندات عبر الرابط. البحث في الويب غير مُعدّ على هذا الخادم.',
  'tasks.form.allowConnectors': 'موصلاتي',
  'tasks.form.allowConnectorsHint': 'يمكنه استخدام: {names}',
  'tasks.form.noConnectors': 'لا توجد موصلات مفعّلة. أضف موصلًا من شريط كتابة الرسائل.',
  'tasks.form.connectorsDisabled': 'الموصلات غير مفعّلة لحسابك، لذا تعمل هذه المهمة بدونها. اطلب الصلاحية من المسؤول.',
  'tasks.form.output': 'التسليم كملف',
  'tasks.form.outputNone': 'بدون ملف — نص فقط',
  'tasks.form.output.docx': 'مستند Word (.docx)',
  'tasks.form.output.xlsx': 'جدول Excel (.xlsx)',
  'tasks.form.output.csv': 'CSV (.csv)',
  'tasks.form.output.md': 'Markdown (.md)',
  'tasks.form.output.html': 'صفحة ويب (.html)',
  'tasks.form.outputHint': 'يُرفق الملف بالنتيجة في المحادثة وفي البريد الإلكتروني.',
  'tasks.form.notify.condition': 'فقط إذا…',
  'tasks.form.notifyCondition': 'شرط الإرسال',
  'tasks.form.notifyConditionPlaceholder': 'مثال: "ينتهي عقد خلال 30 يومًا" أو "ينخفض السعر عن 100"',
  'tasks.form.notifyJudgeHint': 'يتحقق الذكاء الاصطناعي من كل نتيجة ولا يرسل إلا عند الحاجة. تُرسل التشغيلات الفاشلة دائمًا.',
  'tasks.form.pickCondition': 'صف متى تريد أن تصلك رسالة.',
  'tasks.run.steps': '{n} استدعاء أداة',
  'tasks.tool.web_search': 'بحث في الويب',
  'tasks.tool.fetch_url': 'قراءة صفحة',
  'tasks.tool.create_file': 'إنشاء ملف',
  'tasks.draft.to': 'إلى {who}',
  'tasks.draft.me': 'أنت',
  'tasks.draft.people': '{n} زميل',
  'tasks.form.emailInPromptHint': 'كتابة عنوان البريد في الطلب لا ترسل رسالة. اختر متى يُرسل البريد أعلاه وأضف المستلمين.',

  // Templates
  'tasks.tpl.briefing': 'موجز الصباح',
  'tasks.tpl.briefingPrompt': 'قدّم لي موجزًا قصيرًا ليوم {{weekday}} {{date}}: أهم ثلاثة أمور يجب أن أركز عليها اليوم، وأي شيء قد أنساه.',
  'tasks.tpl.weekly': 'ملخص أسبوعي',
  'tasks.tpl.weeklyPrompt': 'لخّص الأسبوع المنتهي في {{date}} في خمس نقاط، ثم اذكر المخاطر المفتوحة والخطوات التالية.',
  'tasks.tpl.changes': 'أخبرني بما تغيّر',
  'tasks.tpl.changesPrompt': 'تحقق مجددًا وأخبرني فقط بما هو جديد أو مختلف مقارنة بإجابتك السابقة. إذا لم يتغير شيء فقل ذلك في سطر واحد.\n\nالإجابة السابقة:\n{{last_result}}',
  'tasks.tpl.standup': 'جدول الاجتماع اليومي',
  'tasks.tpl.standupPrompt': 'أعدّ جدول أعمال لاجتماع يومي مدته 10 دقائق ليوم {{weekday}} {{date}} مع توقيت لكل بند.',

  'tasks.templates.heading': 'مقترحة',
  'tasks.templates.manage': 'إدارة القوالب',
  'tasks.templates.doneManaging': 'تم',
  'tasks.templates.new': 'قالب جديد',
  'tasks.templates.empty': 'لا توجد قوالب بعد.',
  'tasks.templates.atCap': 'وصلت إلى الحد الأقصى للمهام النشطة.',
  'tasks.templates.builtIn': 'مدمج',
  'tasks.templates.hidden': 'مخفي',
  'tasks.templates.edit': 'تعديل القالب {name}',
  'tasks.templates.hide': 'إخفاء القالب {name} عن المستخدمين',
  'tasks.templates.publish': 'إظهار القالب {name} للمستخدمين',
  'tasks.templates.hideShort': 'إخفاء عن المستخدمين',
  'tasks.templates.publishShort': 'إظهار للمستخدمين',
  'tasks.templates.delete': 'حذف القالب {name}',
  'tasks.templates.hiddenToast': 'تم إخفاء القالب عن المستخدمين',
  'tasks.templates.publishedToast': 'أصبح القالب ظاهرًا للمستخدمين',
  'tasks.templates.deleteTitle': 'حذف القالب "{name}"؟',
  'tasks.templates.deleteMessage': 'لن يظهر للمستخدمين بعد الآن. المهام التي أُنشئت منه تستمر في العمل.',
  'tasks.templates.deleted': 'تم حذف القالب',
  'tasks.templates.saved': 'تم حفظ القالب',
  'tasks.templates.newTitle': 'قالب جديد',
  'tasks.templates.editTitle': 'تعديل القالب',
  'tasks.templates.review': 'بدأت من قالب "{name}". راجع التفاصيل ثم أنشئ المهمة.',
  'tasks.templates.editorNote': 'يرى كل من لديه صلاحية المهام القوالب المنشورة ضمن "مقترحة"، ويمكنه إنشاء مهمة منها بنقرة واحدة.',
  'tasks.templates.titleEn': 'الاسم (بالإنجليزية)',
  'tasks.templates.titleAr': 'الاسم (بالعربية، اختياري)',
  'tasks.templates.icon': 'الأيقونة (رمز تعبيري)',
  'tasks.templates.description': 'الوصف',
  'tasks.templates.descriptionPlaceholder': 'سطر واحد عمّا يحصل عليه المستخدم، مثل: "أسعار الحديد والأسمنت أسبوعيًا في جدول بيانات."',
  'tasks.templates.descriptionAr': 'الوصف (بالعربية، اختياري)',
  'tasks.templates.publishedLabel': 'إظهار للمستخدمين',
  'tasks.templates.defaultModel': 'النموذج الافتراضي (الافتراضي لكل مستخدم)',
  'tasks.templates.recipientsHint': 'تُرسل النتائج إلى من ينشئ مهمة من هذا القالب، ويمكنه إضافة مستلمين آخرين.',
  'tasks.templates.save': 'حفظ القالب',

  'tasks.admin.section': 'المهام المجدولة',
  'tasks.admin.enable': 'السماح لهذا المستخدم بإنشاء مهام مجدولة',
  'tasks.admin.max': 'الحد الأقصى للمهام النشطة',
  'tasks.admin.maxDefault': 'الافتراضي ({n})',
  'tasks.admin.maxHint': 'اتركه فارغًا لاستخدام الافتراضي. إيقاف الوصول يوقف مهام هذا المستخدم فورًا.',
  'tasks.admin.inUse': '{active} نشطة من أصل {total} مهمة',
  'tasks.admin.updateFailed': 'تم حفظ المستخدم، لكن تعذّر تحديث صلاحية المهام.',
};

const tasks = { en, ar };
export default tasks;
